import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/CockpitAuthContext';
import './Login.css';

// Helper fetch pra endpoints publicos (sem auth JWT)
const apiPublico = async (path, body) => {
  const r = await fetch('/api' + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await r.json();
  if (!r.ok) throw { response: { data, status: r.status } };
  return { data };
};

export default function Login() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenReset = searchParams.get('reset');
  // [Bruno 2026-10-13] Quem abre o atendimento pelo celular volta pra ele
  // depois de entrar. Só aceita destino interno conhecido (nada de link externo).
  const volta = (() => {
    const v = searchParams.get('volta') || '';
    return /^\/atendimento(\/(nivelon|meu-contador))?$/.test(v) ? v : '/escritorio';
  })();

  const [modo, setModo] = useState(tokenReset ? 'redefinir' : 'login');  // 'login' | 'mfa' | 'esqueci' | 'redefinir'
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [senhaConfirma, setSenhaConfirma] = useState('');
  const [erro, setErro] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [mfaSession, setMfaSession] = useState(null);
  const [mfaCodigo, setMfaCodigo] = useState('');

  const { loginEscritorio, loginEscritorioMfa } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (tokenReset) setModo('redefinir');
  }, [tokenReset]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      const data = await loginEscritorio(email, senha);
      if (data?.requer_mfa) {
        // Senha certa + verificação em 2 passos ativa → pede o código do app.
        setMfaSession(data.mfa_session_token);
        setMfaCodigo('');
        setModo('mfa');
        return;
      }
      navigate(volta);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao fazer login');
    } finally { setCarregando(false); }
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      await loginEscritorioMfa(mfaSession, mfaCodigo);
      navigate(volta);
    } catch (err) {
      // Sessão é single-use: errou/expirou → volta pro passo da senha.
      setErro(err.response?.data?.erro || 'Código inválido — faça o login de novo.');
      setMfaSession(null); setMfaCodigo(''); setModo('login');
    } finally { setCarregando(false); }
  };

  const handleEsqueci = async (e) => {
    e.preventDefault();
    setErro(''); setMensagem('');
    setCarregando(true);
    try {
      const { data } = await apiPublico('/auth/esqueci-senha', { email });
      setMensagem(data.mensagem || 'Verifique seu email.');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao enviar');
    } finally { setCarregando(false); }
  };

  const handleRedefinir = async (e) => {
    e.preventDefault();
    setErro('');
    if (senhaNova !== senhaConfirma) return setErro('Senhas não conferem');
    if (senhaNova.length < 6) return setErro('Mínimo 6 caracteres');
    setCarregando(true);
    try {
      await apiPublico('/auth/redefinir-senha', { token: tokenReset, senha_nova: senhaNova });
      setMensagem('Senha nova salva. Entre com ela.');
      setSearchParams({});
      setModo('login');
      setSenhaNova(''); setSenhaConfirma('');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao redefinir');
    } finally { setCarregando(false); }
  };

  const trocar = (m) => { setModo(m); setErro(''); setMensagem(''); };

  return (
    <div className="lg-root">
      <aside className="lg-marca" aria-label="Marçal Contabilidade">
        <img className="lg-marca__agua" src="/logos/marcal-simbolo-branco.svg" alt="" aria-hidden="true" />
        <img className="lg-marca__logo" src="/logos/marcal-horizontal-branca.svg" alt="Marçal Contabilidade" />
        <div className="lg-marca__meio">
          <span className="lg-marca__selo">Cockpit · painel da equipe</span>
          <h2 className="lg-marca__titulo">O escritório inteiro <em>num só lugar.</em></h2>
          <p className="lg-marca__texto">Fechamentos, notas, folha e atendimento dos clientes, com a ANA trabalhando junto com a equipe.</p>
          <ul className="lg-marca__lista">
            <li><Ic d="M9 11l3 3 8-8M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />Fechamento do Simples e do Presumido</li>
            <li><Ic d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z M8 8h8M8 12h8M8 16h5" />Notas fiscais e guias dos clientes</li>
            <li><Ic d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />Chamados, WhatsApp e chat da equipe</li>
          </ul>
        </div>
        <div className="lg-marca__pe">Marçal Contabilidade · Curitiba/PR</div>
      </aside>

      <main className="lg-lado">
        <div className="lg-card">
          {modo === 'login' && (
            <>
              <div className="lg-card__topo">
                <h1 className="lg-card__titulo">Entrar no Cockpit</h1>
                <p className="lg-card__sub">Use o e-mail (ou o usuário) e a senha da sua conta da equipe.</p>
              </div>
              <Avisos erro={erro} mensagem={mensagem} />
              <form onSubmit={handleLogin} className="lg-form">
                <Campo id="lg-email" rotulo="E-mail ou usuário">
                  <input id="lg-email" className="lg-input" type="text" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username" placeholder="seu@email.com"
                    value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
                </Campo>
                <CampoSenha id="lg-senha" rotulo="Senha" valor={senha} aoMudar={setSenha} autoComplete="current-password" placeholder="Sua senha" />
                <div className="lg-linha">
                  <button type="button" className="lg-link" onClick={() => trocar('esqueci')}>Esqueci minha senha</button>
                </div>
                <Botao carregando={carregando} texto="Entrar" textoCarregando="Entrando" />
              </form>
            </>
          )}

          {modo === 'mfa' && (
            <>
              <div className="lg-card__topo">
                <h1 className="lg-card__titulo">Verificação em 2 passos</h1>
                <p className="lg-card__sub">Digite o código de 6 dígitos do seu app autenticador.</p>
              </div>
              <Avisos erro={erro} />
              <form onSubmit={handleMfa} className="lg-form">
                <Campo id="lg-mfa" rotulo="Código">
                  <input id="lg-mfa" className="lg-input lg-input--codigo" type="text" inputMode="numeric" autoComplete="one-time-code"
                    maxLength={6} placeholder="000000" value={mfaCodigo}
                    onChange={(e) => setMfaCodigo(e.target.value.replace(/\D/g, ''))} autoFocus />
                </Campo>
                <Botao carregando={carregando} desabilitado={mfaCodigo.length !== 6} texto="Confirmar" textoCarregando="Verificando" />
              </form>
              <button type="button" className="lg-link lg-voltar" onClick={() => { trocar('login'); setMfaSession(null); setMfaCodigo(''); }}>
                Voltar
              </button>
            </>
          )}

          {modo === 'esqueci' && (
            <>
              <div className="lg-card__topo">
                <h1 className="lg-card__titulo">Esqueci minha senha</h1>
                <p className="lg-card__sub">Enviamos um link para você criar uma senha nova.</p>
              </div>
              <Avisos erro={erro} mensagem={mensagem} />
              <form onSubmit={handleEsqueci} className="lg-form">
                <Campo id="lg-email-rec" rotulo="E-mail cadastrado">
                  <input id="lg-email-rec" className="lg-input" type="email" autoComplete="username" placeholder="seu@email.com"
                    value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
                </Campo>
                <Botao carregando={carregando} texto="Enviar link" textoCarregando="Enviando" />
              </form>
              <button type="button" className="lg-link lg-voltar" onClick={() => trocar('login')}>Voltar para o login</button>
            </>
          )}

          {modo === 'redefinir' && (
            <>
              <div className="lg-card__topo">
                <h1 className="lg-card__titulo">Criar senha nova</h1>
                <p className="lg-card__sub">Use pelo menos 6 caracteres.</p>
              </div>
              <Avisos erro={erro} />
              <form onSubmit={handleRedefinir} className="lg-form">
                <CampoSenha id="lg-nova" rotulo="Nova senha" valor={senhaNova} aoMudar={setSenhaNova} autoComplete="new-password" minLength={6} autoFocus />
                <CampoSenha id="lg-confirma" rotulo="Repita a nova senha" valor={senhaConfirma} aoMudar={setSenhaConfirma} autoComplete="new-password" minLength={6} />
                <Botao carregando={carregando} texto="Salvar senha" textoCarregando="Salvando" />
              </form>
            </>
          )}
        </div>

        <div className="lg-rodape"><span className="lg-rodape__ponto" aria-hidden="true" />Sistema operacional · acesso restrito à equipe</div>
      </main>
    </div>
  );
}

function Ic({ d }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d={d} /></svg>;
}

function Campo({ id, rotulo, children }) {
  return (
    <div className="lg-campo">
      <label className="lg-campo__rot" htmlFor={id}>{rotulo}</label>
      <div className="lg-campo__caixa">{children}</div>
    </div>
  );
}

function CampoSenha({ id, rotulo, valor, aoMudar, autoComplete, placeholder = '••••••', minLength, autoFocus }) {
  const [ver, setVer] = useState(false);
  return (
    <Campo id={id} rotulo={rotulo}>
      <input id={id} className="lg-input lg-input--com-botao" type={ver ? 'text' : 'password'} autoComplete={autoComplete}
        placeholder={placeholder} value={valor} onChange={(e) => aoMudar(e.target.value)} required minLength={minLength} autoFocus={autoFocus} />
      <button type="button" className="lg-olho" onClick={() => setVer((v) => !v)}
        aria-label={ver ? 'Esconder senha' : 'Mostrar senha'} aria-pressed={ver} title={ver ? 'Esconder senha' : 'Mostrar senha'}>
        {ver
          ? <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.9 4.2A10 10 0 0 1 12 4c6.5 0 10 8 10 8a17.6 17.6 0 0 1-2.2 3.2M6.6 6.6A17.5 17.5 0 0 0 2 12s3.5 8 10 8a9.7 9.7 0 0 0 5.4-1.6M2 2l20 20M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>
          : <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-8 10-8 10 8 10 8-3.5 8-10 8S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>}
      </button>
    </Campo>
  );
}

function Botao({ carregando, desabilitado, texto, textoCarregando }) {
  return (
    <button type="submit" className="lg-botao" disabled={carregando || desabilitado}>
      {carregando
        ? <><span className="lg-girando" aria-hidden="true" />{textoCarregando}…</>
        : <>{texto}<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg></>}
    </button>
  );
}

function Avisos({ erro, mensagem }) {
  return (
    <>
      {erro && <div className="lg-aviso lg-aviso--erro" role="alert">{erro}</div>}
      {mensagem && <div className="lg-aviso lg-aviso--ok" role="status">{mensagem}</div>}
    </>
  );
}
